import { ESRCH } from "constants";
import { LocationQueryValue } from "vue-router";
import { Commit } from "vuex";
import { http } from "../../plugins/axios";
import { UserAddress } from "../../types/UserAddress.type";
import { UserContact } from "../../types/UserContact";

export interface UsersState {
  contacts: Array<any>;
  addresses: Array<any>;
  bankAccounts: Array<any>;
  localOffices: Array<any>;
  paymentTypes: Array<any>;
  authUser: object | null;
  // Tracks the OUTCOME of fetching authUser, which authUser itself cannot express:
  // its initial value is {} and a failed fetch never commits, so {} means both
  // "not loaded yet" and "load failed". Anything that needs to tell a claimant WHY
  // their profile is missing has to read this instead.
  authUserStatus: "idle" | "loading" | "ready" | "error";
}

const state = (): UsersState => ({
  contacts: [],
  addresses: [],
  bankAccounts: [],
  localOffices: [],
  paymentTypes: [],
  authUser: {},
  authUserStatus: "idle",
});

const userService = import.meta.env.VITE_USER_SERVICE_URL;
const cardService = import.meta.env.VITE_CARD_SERVICE_URL;
const demographicService = import.meta.env.VITE_DEMOGRAPHIC_SERVICE_URL;

const mutations = {
  STORE_CONTACTS(state: UsersState, contacts: UsersState["contacts"]) {
    state.contacts = contacts;
  },
  STORE_ADDRESSES(state: UsersState, addresses: UsersState["addresses"]) {
    state.addresses = addresses;
  },
  STORE_BANK_ACCOUNTS(
    state: UsersState,
    bankAccounts: UsersState["bankAccounts"]
  ) {
    state.bankAccounts = bankAccounts;
  },
  STORE_LOCAL_OFFICES(
    state: UsersState,
    localOffices: UsersState["localOffices"]
  ) {
    state.localOffices = localOffices;
  },
  STORE_PAYMENT_TYPES(
    state: UsersState,
    paymentTypes: UsersState["paymentTypes"]
  ) {
    state.paymentTypes = paymentTypes;
  },
  STORE_AUTH_USER(state: UsersState, authUser: UsersState["authUser"]) {
    state.authUser = authUser;
  },
  STORE_AUTH_USER_STATUS(
    state: UsersState,
    status: UsersState["authUserStatus"]
  ) {
    state.authUserStatus = status;
  },
};

const getters = {
  getContacts(state: UsersState): UsersState["contacts"] {
    return state.contacts;
  },
  getAddresses(state: UsersState): UsersState["addresses"] {
    return state.addresses;
  },
  getBankAccounts(state: UsersState): UsersState["bankAccounts"] {
    return state.bankAccounts;
  },
  getLocalOffices(state: UsersState): UsersState["localOffices"] {
    return state.localOffices;
  },
  getPaymentTypes(state: UsersState): UsersState["paymentTypes"] {
    return state.paymentTypes;
  },
  getAuthUser(state: UsersState): UsersState["authUser"] {
    return state.authUser;
  },
  getAuthUserStatus(state: UsersState): UsersState["authUserStatus"] {
    return state.authUserStatus;
  },
};

const actions = {
  async register(_ctx: any, data: any) {
    const endpoint = `${userService}/signup`;

    const res = await http.post(endpoint, data);

    return res;
  },
  async getUserPhoneTypes() {
    const endpoint = `${demographicService}/claimant-contact/phone/types`;

    const res = await http.get(endpoint);

    return res.data;
  },
  async getUserAddressTypes() {
    const endpoint = `${demographicService}/claimant-address/types`;

    const res = await http.get(endpoint);

    return res.data;
  },
  async getCountriesAndStates() {
    const endpoint = `${demographicService}/claimant-address/states`;

    const res = await http.get(endpoint);

    return res.data;
  },
  async getAllCountries() {
    const endpoint = `${demographicService}/claimant-contact/countries`;

    const res = await http.get(endpoint);

    return res.data.countries;
  },
  async getUserContacts({ commit }: { commit: Commit }) {
    const endpoint = `${demographicService}/claimant-contact`;

    const res = await http.get(endpoint);

    commit(
      "STORE_CONTACTS",
      res.data.contacts.sort((a: any, b: any) => b.primaryContact - a.primaryContact)
    );

    return res.data;
  },
  async getUserAddressBook({ commit }: { commit: Commit }) {
    const endpoint = `${demographicService}/claimant-address`;

    const res = await http.get(endpoint);

    commit("STORE_ADDRESSES", res.data.addresses);

    return res.data;
  },
  async postNewUserAddress(
    { commit }: { commit: Commit },
    payload: UserAddress
  ) {
    const endpoint = `${demographicService}/claimant-address`;

    const res = await http.post(endpoint, payload);

    commit("STORE_ADDRESSES", res.data.addresses);

    return res.data;
  },
  async postUserContact({ commit }: { commit: Commit }, payload: UserContact) {
    const endpoint = `${demographicService}/claimant-contact`;

    const res = await http.post(endpoint, payload);

    commit("STORE_CONTACTS", res.data.contacts);

    return res.data;
  },
  async setContactAsPrimary(
    { commit }: { commit: Commit },
    contactId: number | string
  ) {
    const endpoint = `${demographicService}/claimant-contact/${contactId}/primary`;

    const res = await http.put(endpoint);

    return res.data;
  },
  async forgotPassword(_ctx: any, payload: { eeni: number }) {
    const endpoint = `${userService}/forgot/password`;

    const res = await http.post(endpoint, payload);

    return res.data;
  },
  async resetUserPassword(
    {},
    payload: {
      password: string;
      token: LocationQueryValue | LocationQueryValue[];
    }
  ) {
    const endpoint = `${userService}/reset/password?token=${payload.token}`;
    const res = await http.put(endpoint, { password: payload.password });

    return res.data;
  },
  async resendActivationEmail({}, payload: { eeni: string }) {
    const endpoint = `${userService}/resend/activation`;
    const res = await http.post(endpoint, payload);

    return res.data;
  },
  async activateUserAccount({}, payload: string) {
    const endpoint = `${userService}/activate?token=${payload}`;

    const res = await http.post(endpoint, {});

    return res.data;
  },
  async getUserBankAccounts({ commit }: { commit: Commit }) {
    const endpoint = `${demographicService}/banking-details`;

    const res = await http.get(endpoint);

    commit("STORE_BANK_ACCOUNTS", res.data.bankingDetails);

    return res.data;
  },

  async addUserBankAccount({ commit }: { commit: Commit }, payload: any) {
    const endpoint = `${demographicService}/banking-details`;

    const res = await http.post(endpoint, payload);

    commit("STORE_BANK_ACCOUNTS", res.data.bankingDetails);

    return res.data;
  },
  async getLocalOffices({ commit }: { commit: Commit }) {
    const endpoint = `${demographicService}/local-offices`;

    const res = await http.get(endpoint);

    commit("STORE_LOCAL_OFFICES", res.data.localOffices);

    return res.data;
  },
  async getPaymentTypes({ commit }: { commit: Commit }) {
    const endpoint = `/application/payment-type`;

    const res = await http.get(endpoint);

    commit("STORE_PAYMENT_TYPES", res.data.payments_type);

    return res.data;
  },
  async changeLocalOffice({ commit }: { commit: Commit }, localOffice: any) {
    const endpoint = `${demographicService}/user/local-office`;

    const res = await http.post(endpoint, localOffice);

    return res.data;
  },
  async changeUserPassword(
    { commit }: { commit: Commit },
    payload: { password: string }
  ) {
    const endpoint = `${userService}/reset/password`;

    const res = await http.put(endpoint, payload);

    return res.data;
  },
  async changeUserEmail(
    { commit }: { commit: Commit },
    payload: { email: string }
  ) {
    const endpoint = `${userService}/user/email`;

    const res = await http.post(endpoint, payload);

    return res.data;
  },
  async updateUserEmail(
    { commit }: { commit: Commit },
    payload: { token: string }
  ) {
    const endpoint = `${userService}/user/email?token=${payload.token}`;

    const res = await http.put(endpoint, {});

    return res.data;
  },
  async getAuthUser({ commit }: { commit: Commit }) {
    const endpoint = `${demographicService}/user`;

    commit("STORE_AUTH_USER_STATUS", "loading");
    try {
      const res = await http.get(endpoint);

      commit("STORE_AUTH_USER", res.data.user);
      commit("STORE_AUTH_USER_STATUS", "ready");

      return res.data.user;
    } catch (err) {
      commit("STORE_AUTH_USER_STATUS", "error");
      // Rethrown deliberately: every existing caller reaches this through
      // useUserData().getAuthUser(), which rejects, and swallowing it here would
      // silently change that contract. This only ADDS the status.
      throw err;
    }
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions,
};
